import { useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  IconArrowRight,
  IconSparkle,
  IconStar,
} from '@/components/home/icons';

/**
 * /find-qari — browse and discover Quran teachers.
 *
 * Note on data: every Qari shown here is a curated illustrative
 * placeholder, not a real instructor profile. A future iteration will
 * source these from the `qari_profiles` table (RLS-filtered to visible
 * profiles) — for now the page is a styled showcase. The progress
 * widget that used to live in the sidebar has been removed since it
 * showed fake "Surah Al-Baqarah 64%" data that wasn't tied to any
 * real reading_progress query.
 *
 * Avatars are stylized SVG illustrations from /public/qaris/. They
 * are NOT photographs of real people — they're hand-drawn portrait
 * icons designed to read as "different bearded male teachers" without
 * making any claim to real identities.
 */

type Qari = {
  name: string;
  specialty: string;
  rating: number;
  students: number;
  tags: string[];
  online: boolean;
  featured?: boolean;
  avatar: string;
  /**
   * Female teachers (Qaria). When true, the avatar receives a CSS
   * blur filter on render per the modesty-respecting display norm
   * the product follows. The data + name stay intact; only the
   * portrait itself is softened. The blur is purely visual — alt
   * text, search, RLS, etc. are unchanged.
   */
  isFemale?: boolean;
};

const QARIS: Qari[] = [
  {
    name: 'Qari Ahmed',
    specialty: 'Hifz Specialist',
    rating: 4.9,
    students: 128,
    tags: ['Qalon', 'Ijazah Holder', 'Arabic Bilingual'],
    online: true,
    featured: true,
    avatar: '/qaris/qari-ahmed.webp',
  },
  {
    name: 'Qari Yusuf',
    specialty: 'Tajweed Expert',
    rating: 5.0,
    students: 245,
    tags: ['Child Friendly', 'English Fluent', 'Modern Methods'],
    online: false,
    avatar: '/qaris/qari-yusuf.webp',
  },
  {
    name: 'Qaria Maryam',
    specialty: 'Tafsir Scholar',
    rating: 4.8,
    students: 92,
    tags: ['Azhar Graduate', 'Academic Style', 'Multilingual'],
    online: true,
    avatar: '/qaris/qaria-maryam.webp',
    isFemale: true,
  },
  {
    name: 'Qari Faisal',
    specialty: 'Youth Mentor & Hifz',
    rating: 4.9,
    students: 182,
    tags: ['Gamified Learning', 'Native English', 'Fast Track'],
    online: true,
    avatar: '/qaris/qari-faisal.webp',
  },
];

const MATCHES: { name: string; score: number; avatar: string; isFemale?: boolean }[] = [
  { name: 'Qaria Maryam', score: 98, avatar: '/qaris/qaria-maryam.webp', isFemale: true },
  { name: 'Qari Ahmed', score: 92, avatar: '/qaris/qari-ahmed.webp' },
  { name: 'Qari Yusuf', score: 85, avatar: '/qaris/qari-yusuf.webp' },
];

type FilterGroup = {
  label: string;
  options: string[];
};

const FILTER_GROUPS: FilterGroup[] = [
  { label: 'Language', options: ['Arabic', 'English', 'Urdu'] },
  { label: 'Mastery', options: ['Hifz', 'Tajweed', 'Tafsir'] },
  { label: 'Style', options: ['Accelerated', 'Academic'] },
];

function QariCard({ qari }: { qari: Qari }) {
  return (
    <div className="group flex h-full flex-col rounded-xl border border-night-500/30 bg-night-700 p-6 transition-all hover:border-beam hover:shadow-[0_0_15px_rgba(0,229,255,0.15)]">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex gap-4">
          <div className="relative h-16 w-16 overflow-hidden rounded-xl border border-night-500/50 bg-night-500">
            <img
              src={qari.avatar}
              alt={'Portrait of ' + qari.name}
              // Female (Qaria) portraits are blurred per modesty norms.
              // The data, name, and rating are all preserved — only the
              // photograph is softened. blur(10px) preserves silhouette
              // shape + colors but obscures facial features.
              className={
                'h-full w-full object-cover ' +
                (qari.isFemale ? 'blur-[10px] scale-110' : '')
              }
              loading="lazy"
              width={64}
              height={64}
            />
            <div
              className={
                'absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-night-900 ' +
                (qari.online ? 'bg-green-500' : 'bg-night-500')
              }
              aria-label={qari.online ? 'Online' : 'Offline'}
            />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold leading-tight text-beam">
              {qari.name}
            </h3>
            <p className="text-sm text-night-200">{qari.specialty}</p>
            <div className="mt-1 flex items-center gap-1 text-beam-soft">
              <IconStar className="h-4 w-4" aria-hidden="true" />
              <span className="text-xs font-bold">{qari.rating.toFixed(1)}</span>
              <span className="ml-1 text-[10px] text-night-200">
                ({qari.students} Students)
              </span>
            </div>
          </div>
        </div>
        {qari.featured && (
          <span className="rounded bg-beam/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-beam">
            Featured
          </span>
        )}
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {qari.tags.map((tag) => (
          <span
            key={tag}
            className="rounded border border-night-500/50 px-2 py-0.5 text-[10px] uppercase tracking-wide text-night-200"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-auto flex gap-3">
        <button
          type="button"
          className="flex-1 rounded-lg border border-beam py-2.5 text-sm font-bold text-beam transition-all hover:bg-beam/10"
        >
          View Profile
        </button>
        <button
          type="button"
          className="flex-1 rounded-lg bg-beam py-2.5 text-sm font-bold text-beam-ink transition-all hover:brightness-110"
        >
          Book Trial
        </button>
      </div>
    </div>
  );
}

export function FindQari() {
  useDocumentTitle('Find a Qari');

  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    new Set(['Arabic', 'Tajweed']),
  );
  const [query, setQuery] = useState('');

  const toggleFilter = (option: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      return next;
    });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-night-900 text-night-50">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -left-24 top-20 select-none font-display text-[200px] font-black leading-none tracking-tight text-night-50/[0.03]"
      >
        QARI
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -right-24 bottom-20 rotate-90 select-none font-display text-[200px] font-black leading-none tracking-tight text-night-50/[0.03]"
      >
        KNOWLEDGE
      </div>

      <main className="relative z-10 mx-auto max-w-[1280px] px-6 pb-24 pt-12 lg:px-12">
        {/* Hero */}
        <section className="mb-20 flex flex-col items-center text-center">
          <h1 className="mb-4 font-display text-4xl font-extrabold leading-tight tracking-tight lg:text-6xl">
            Elevate Your <span className="text-beam">Quranic Journey</span>
          </h1>
          <p className="mb-12 max-w-2xl text-night-200">
            Connect with world-class Qaris through our AI-powered matchmaking system.
            Personalized learning paths for Hifz, Tajweed, and Tafsir.
          </p>
          <div className="group relative w-full max-w-3xl">
            <div
              aria-hidden="true"
              className="absolute -inset-1 rounded-full bg-gradient-to-r from-beam via-beam-soft to-beam opacity-25 blur transition duration-1000 group-hover:opacity-60 group-hover:duration-200"
            />
            <form
              className="relative flex items-center rounded-full border border-night-500/40 bg-night-950 p-2"
              onSubmit={(e) => e.preventDefault()}
            >
              <IconSparkle className="ml-4 h-5 w-5 text-beam" aria-hidden="true" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find your ideal Qari with AI…"
                className="w-full border-none bg-transparent px-4 py-3 text-night-50 placeholder:text-night-300 focus:outline-none focus:ring-0"
              />
              <button
                type="submit"
                className="mr-1 rounded-full bg-beam px-8 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-beam-ink transition-all hover:scale-105"
              >
                Search
              </button>
            </form>
          </div>
        </section>

        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="flex-1">
            {/* Filters */}
            <div className="mb-10 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-beam">
                  Active Filters
                </h2>
                <button
                  type="button"
                  onClick={() => setActiveFilters(new Set())}
                  className="text-sm text-beam hover:underline"
                >
                  Clear all
                </button>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto pb-4">
                <div className="flex min-w-max items-center gap-2">
                  {FILTER_GROUPS.map((group, idx) => (
                    <div
                      key={group.label}
                      className={
                        'flex gap-2 ' +
                        (idx < FILTER_GROUPS.length - 1
                          ? 'mr-2 border-r border-night-500/30 pr-4'
                          : '')
                      }
                    >
                      {group.options.map((option) => {
                        const active = activeFilters.has(option);
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => toggleFilter(option)}
                            className={
                              'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ' +
                              (active
                                ? 'border-beam bg-beam text-beam-ink'
                                : 'border-night-500/30 bg-night-800 text-night-200 hover:border-beam/50')
                            }
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Discovery grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {QARIS.map((qari) => (
                <QariCard key={qari.name} qari={qari} />
              ))}
            </div>
          </div>

          {/* AI Matchmaker sidebar */}
          <aside className="flex flex-col gap-6 lg:w-80">
            <div className="rounded-2xl border border-night-500/30 border-l-4 border-l-beam bg-night-950/70 p-6 backdrop-blur-md">
              <div className="mb-4 flex items-center gap-2">
                <IconSparkle className="h-5 w-5 text-beam" aria-hidden="true" />
                <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-night-50">
                  AI Matchmaker
                </h2>
              </div>
              <p className="mb-4 text-xs leading-relaxed text-night-200">
                Based on your interest in{' '}
                <span className="text-beam">Tajweed Mastery</span> and your{' '}
                <span className="text-beam">Weekend Morning</span> availability.
              </p>
              <div className="space-y-4">
                {MATCHES.map((match) => (
                  <button
                    key={match.name}
                    type="button"
                    className="group flex w-full cursor-pointer items-center gap-3 rounded-xl bg-night-600/50 p-3 text-left transition-colors hover:bg-night-600"
                  >
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-beam/20 bg-night-500">
                      <img
                        src={match.avatar}
                        alt={'Portrait of ' + match.name}
                        // Same modesty-blur rule as the main cards.
                        className={
                          'h-full w-full object-cover ' +
                          (match.isFemale ? 'blur-[8px] scale-110' : '')
                        }
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-sm font-bold text-night-50">
                        {match.name}
                      </h4>
                      <p className="text-[10px] text-beam">{match.score}% Match Score</p>
                    </div>
                    <IconArrowRight
                      className="h-4 w-4 text-night-200 transition-colors group-hover:text-beam"
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="mt-6 w-full rounded-lg border border-night-500/50 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-night-200 transition-all hover:border-beam/50 hover:text-beam"
              >
                Recalibrate AI
              </button>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
